<template>
  <a-drawer width="45%" :label-col="6" :wrapper-col="16" :visible="open" @close="onClose">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-alert
      v-if="formType === 1"
      type="info"
      show-icon
      style="margin-bottom: 16px;"
      message="后台手动新增的订单为「只读展示」订单"
      description="师傅端可见但不可抢单、不可查看详情（展示为已满员）；无保险、无诚意金、无支付流水；归属平台自营账号。"
    />
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="订单类型" prop="type">
            <a-select v-model="form.type" placeholder="请选择订单类型">
              <a-select-option :value="0">找个人</a-select-option>
              <a-select-option :value="1">找企业</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="招工标题" prop="title">
            <a-input v-model="form.title" placeholder="请输入招工标题" allow-clear />
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="工种" prop="categoryIds">
            <a-select
              v-model="form.categoryIds"
              mode="multiple"
              placeholder="可输入关键词搜索工种（可多选）"
              show-search
              option-filter-prop="children"
              :filter-option="filterCategoryOption"
              allow-clear
            >
              <a-select-option v-for="item in categoryList" :key="item.id" :value="String(item.id)">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="计价方式" prop="pricingMethod">
            <a-select v-model="form.pricingMethod" placeholder="请选择计价方式" @change="onPricingMethodChange">
              <a-select-option :value="0">议价</a-select-option>
              <a-select-option :value="3">按量计价</a-select-option>
              <a-select-option :value="4">一口价</a-select-option>
              <a-select-option :value="5">按时/天计价</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="金额" prop="money">
            <a-input-number v-model="form.money" :min="0" :precision="2" style="width: 100%" placeholder="单价/总价" />
          </a-form-model-item>
        </a-col>
        <a-col v-if="showUnit" :span="12">
          <a-form-model-item :label="unitLabel" prop="unit">
            <a-select v-if="form.pricingMethod === 5" v-model="form.unit" placeholder="请选择计价单位">
              <a-select-option value="时">时</a-select-option>
              <a-select-option value="天">天</a-select-option>
            </a-select>
            <a-input v-else v-model="form.unit" :placeholder="unitPlaceholder" allow-clear />
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col v-if="form.pricingMethod === 3" :span="12">
          <a-form-model-item label="预估工作量" prop="perCapitaProduction">
            <a-input v-model="form.perCapitaProduction" placeholder="必填，作为总量参与金额计算，如：1000" allow-clear />
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="师傅人数">
            <a-input-number v-model="form.numberOfWorkers" :min="0" :precision="0" style="width: 100%" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="工作要求">
            <a-select
              v-model="form.jobRequirementIds"
              mode="multiple"
              placeholder="请选择工作要求（可多选）"
              allow-clear
            >
              <a-select-option v-for="item in jobRequirementList" :key="item.value" :value="item.value">
                {{ item.text }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row v-if="showUnderstaffed" :gutter="16">
        <a-col :span="24">
          <a-form-model-item label="人员未满是否正常开工" prop="startWorkIfUnderstaffed">
            <a-radio-group v-model="form.startWorkIfUnderstaffed">
              <a-radio :value="1">是</a-radio>
              <a-radio :value="0">否</a-radio>
            </a-radio-group>
            <div style="color: #999; font-size: 12px;">
              仅「按量计价 / 按时-天计价」且招工人数大于 1 时生效（与雇主端一致）
            </div>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="工作开始日期" prop="workStartTime">
            <a-date-picker
              v-model="form.workStartTime"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              placeholder="请选择工作开始日期"
              style="width: 100%"
            />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="工作结束日期" prop="workEndTime">
            <a-date-picker
              v-model="form.workEndTime"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              placeholder="请选择工作结束日期"
              style="width: 100%"
            />
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model-item label="每日开始时间" prop="dailyStartTime">
            <a-time-picker v-model="form.dailyStartTime" format="HH:mm" valueFormat="HH:mm" placeholder="如 08:00" style="width: 100%" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="每日结束时间" prop="dailyEndTime">
            <a-time-picker v-model="form.dailyEndTime" format="HH:mm" valueFormat="HH:mm" placeholder="如 18:00" style="width: 100%" />
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-form-model-item label="地址" prop="address">
        <a-input v-model="form.address" placeholder="请输入详细地址，如：xx市xx区xx路xx号xx小区" allow-clear />
      </a-form-model-item>

      <a-form-model-item label="内容描述">
        <a-input v-model="form.content" type="textarea" :rows="3" placeholder="请输入内容描述" allow-clear />
      </a-form-model-item>

      <a-form-model-item label="上传图片">
        <file-upload type="image" :defaultList="pictureArr" :count="1" @input="getPicture"></file-upload>
        <div style="margin-top: 8px; color: #999; font-size: 12px;">
          最多上传 1 张图片，支持 jpg、png 格式
        </div>
      </a-form-model-item>

      <a-form-model-item label="备注">
        <a-input v-model="form.remark" type="textarea" :rows="2" placeholder="请输入备注" allow-clear />
      </a-form-model-item>

      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            保存
          </a-button>
          <a-button type="dashed" @click="cancel">
            取消
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { manualAddOrder, manualEditOrder, getOrder } from '@/api/order/order'
import { listCategory } from '@/api/category/category'
import { getDicts } from '@/api/system/dict/data'

const defaultForm = () => ({
  id: null,
  type: 0,
  title: null,
  fileType: 1,
  picture: null,
  content: null,
  categoryIds: [],
  category: null,
  address: null,
  pricingMethod: null,
  workStartTime: null,
  workEndTime: null,
  dailyStartTime: '08:00',
  dailyEndTime: '18:00',
  workTime: null,
  money: null,
  unit: null,
  perCapitaProduction: null,
  numberOfWorkers: 1,
  startWorkIfUnderstaffed: null,
  // 工作要求：字典 job_requirement 的 id 多选，提交时 join(',') 写入 jobRequirements
  jobRequirementIds: [],
  jobRequirements: null,
  remark: null
})

export default {
  name: 'ManualCreateForm',
  data () {
    return {
      submitLoading: false,
      formTitle: '',
      form: defaultForm(),
      // 1增加,2修改
      formType: 1,
      open: false,
      categoryList: [],
      jobRequirementList: [],
      pictureArr: [],
      rules: {
        type: [{ required: true, message: '订单类型不能为空', trigger: 'change' }],
        title: [{ required: true, message: '招工标题不能为空', trigger: 'blur' }],
        categoryIds: [{ required: true, message: '工种不能为空', trigger: 'change' }],
        pricingMethod: [{ required: true, message: '计价方式不能为空', trigger: 'change' }],
        money: [{ required: true, message: '金额不能为空', trigger: 'blur' }],
        workStartTime: [{ required: true, message: '工作开始时间不能为空', trigger: 'change' }],
        workEndTime: [{ required: true, message: '工作结束时间不能为空', trigger: 'change' }],
        dailyStartTime: [{ required: true, message: '每日开始时间不能为空', trigger: 'change' }],
        dailyEndTime: [{ required: true, message: '每日结束时间不能为空', trigger: 'change' }],
        address: [{ required: true, message: '地址不能为空', trigger: 'blur' }]
      }
    }
  },
  computed: {
    /** 单位字段：仅按量计价(3)、按时/天计价(5) 需要，与雇主端一致 */
    showUnit () {
      return this.form.pricingMethod === 3 || this.form.pricingMethod === 5
    },
    unitLabel () {
      return this.form.pricingMethod === 5 ? '计价单位' : '单位'
    },
    unitPlaceholder () {
      return this.form.pricingMethod === 3 ? '如：件、吨' : '选填'
    },
    /** 人员未满是否正常开工：按量/按时天计价 且 人数>1 时显示并必填（对齐雇主端 validateStartWorkIfUnderstaffed） */
    showUnderstaffed () {
      const pm = this.form.pricingMethod
      if (pm !== 3 && pm !== 5) {
        return false
      }
      return (Number(this.form.numberOfWorkers) || 0) > 1
    }
  },
  created () {
    this.loadCategory()
    this.loadJobRequirements()
  },
  methods: {
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.formType = 1
      this.form = defaultForm()
      this.pictureArr = []
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetFields()
        }
      })
    },
    loadCategory () {
      listCategory().then(res => {
        this.categoryList = (res.data || [])
      }).catch(() => {
        this.categoryList = []
      })
    },
    /** 工作要求：取自字典 job_requirement，选项值为字典数据 id（与雇主端取值一致） */
    loadJobRequirements () {
      getDicts('job_requirement').then(res => {
        this.jobRequirementList = (res.data || []).map(item => ({
          value: String(item.id),
          text: item.dictLabel
        }))
      }).catch(() => {
        this.jobRequirementList = []
      })
    },
    /** 工种下拉：按工种名称模糊搜索 */
    filterCategoryOption (input, option) {
      const children = option.componentOptions && option.componentOptions.children
      const text = (children && children[0] && children[0].text) || ''
      return String(text).toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
    },
    onPricingMethodChange () {
      if (this.form.pricingMethod === 5) {
        this.form.unit = '时'
      } else {
        this.form.unit = null
      }
      // 仅按量计价使用预估工作量，切走后清空，避免残留脏值入库
      if (this.form.pricingMethod !== 3) {
        this.form.perCapitaProduction = null
      }
      // 人员未满是否开工随计价方式联动，切走后清空（与雇主端一致）
      if (this.form.pricingMethod !== 3 && this.form.pricingMethod !== 5) {
        this.form.startWorkIfUnderstaffed = null
      }
    },
    /** 上传图片回调 */
    getPicture (info) {
      if (typeof info === 'string') {
        this.form.picture = info || null
        this.pictureArr = info ? [{ uid: 'pic', status: 'done', url: info, path: info, halfUrl: info }] : []
        return
      }
      if (!info || !Array.isArray(info) || info.length === 0) {
        this.form.picture = null
        this.pictureArr = []
        return
      }
      const urls = info.map(ele => ele.url || ele.path || ele.halfUrl).filter(Boolean)
      this.form.picture = urls.length > 0 ? urls.join(',') : null
      this.pictureArr = info
    },
    /** 新增按钮操作 */
    handleAdd () {
      this.reset()
      this.formType = 1
      this.open = true
      this.formTitle = '新增招工订单（后台手动）'
    },
    /** 修改按钮操作 */
    handleUpdate (row) {
      this.reset()
      this.formType = 2
      getOrder({ id: row.id }).then(response => {
        this.fillForm(response.data)
        this.open = true
        this.formTitle = '修改招工订单（后台手动）'
      })
    },
    fillForm (data) {
      if (!data) {
        return
      }
      this.form.id = data.id
      this.form.type = data.type
      this.form.title = data.title
      this.form.fileType = data.fileType == null ? 1 : data.fileType
      this.form.picture = data.picture
      this.pictureArr = data.picture ? [{ uid: 'pic', status: 'done', url: data.picture, path: data.picture, halfUrl: data.picture }] : []
      this.form.content = data.content
      this.form.category = data.category
      this.form.categoryIds = data.category ? String(data.category).split(',').map(s => s.trim()).filter(Boolean) : []
      this.form.address = data.address
      this.form.pricingMethod = data.pricingMethod
      this.form.workStartTime = this.toDateStr(data.workStartTime)
      this.form.workEndTime = this.toDateStr(data.workEndTime)
      const seg = this.parseWorkTime(data.workTime)
      this.form.dailyStartTime = seg.startTime || '08:00'
      this.form.dailyEndTime = seg.endTime || '18:00'
      this.form.money = data.money
      this.form.unit = data.unit
      this.form.perCapitaProduction = data.perCapitaProduction
      this.form.numberOfWorkers = data.numberOfWorkers
      this.form.startWorkIfUnderstaffed = data.startWorkIfUnderstaffed
      // 工作要求：库里是逗号分隔的字典 id，回显为多选数组
      this.form.jobRequirementIds = data.jobRequirements
        ? String(data.jobRequirements).split(',').map(s => s.trim()).filter(Boolean)
        : []
      this.form.remark = data.remark
    },
    /** 从 workTime JSON 中解析出每日时段 */
    parseWorkTime (workTime) {
      if (!workTime) {
        return { startTime: null, endTime: null }
      }
      try {
        const arr = JSON.parse(workTime)
        if (Array.isArray(arr) && arr.length > 0) {
          const first = arr[0]
          if (first.startTime && first.endTime) {
            return { startTime: first.startTime, endTime: first.endTime }
          }
          if (typeof first.workTime === 'string' && first.workTime.indexOf('-') > 0) {
            const parts = first.workTime.split('-')
            return { startTime: parts[0], endTime: parts[1] }
          }
        }
      } catch (e) {
        if (typeof workTime === 'string' && workTime.indexOf('-') > 0) {
          const parts = workTime.split('-')
          return { startTime: parts[0], endTime: parts[1] }
        }
      }
      return { startTime: null, endTime: null }
    },
    /** 'YYYY-MM-DD' → 'YYYY-MM-DD 00:00:00'；空值原样返回 */
    toDayStart (dateStr) {
      if (!dateStr) {
        return null
      }
      return /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr} 00:00:00` : dateStr
    },
    /** 'YYYY-MM-DD HH:mm:ss' → 'YYYY-MM-DD'（回显给纯日期选择器） */
    toDateStr (dateTimeStr) {
      return dateTimeStr ? String(dateTimeStr).slice(0, 10) : null
    },
    /** 提交按钮 */
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }
        // 人员未满是否正常开工：显示时必选，隐藏时不提交（对齐雇主端）
        if (this.showUnderstaffed && (this.form.startWorkIfUnderstaffed === null || this.form.startWorkIfUnderstaffed === undefined)) {
          this.$message.warning('请选择人员未满是否正常开工')
          return false
        }
        const jobRequirementIds = this.form.jobRequirementIds || []
        const payload = {
          ...this.form,
          category: (this.form.categoryIds || []).join(','),
          // 工作要求：多选数组的字典 id 拼接成逗号分隔串，与雇主端存法一致
          jobRequirements: jobRequirementIds.length > 0 ? jobRequirementIds.join(',') : null,
          startWorkIfUnderstaffed: this.showUnderstaffed ? this.form.startWorkIfUnderstaffed : null,
          // 前端只选日期，后端 LocalDateTime 需补零时刻（金额/天数计算只取 toLocalDate，不受影响）
          workStartTime: this.toDayStart(this.form.workStartTime),
          workEndTime: this.toDayStart(this.form.workEndTime),
          workTime: JSON.stringify([{ startTime: this.form.dailyStartTime, endTime: this.form.dailyEndTime }])
        }
        delete payload.categoryIds
        delete payload.jobRequirementIds
        delete payload.dailyStartTime
        delete payload.dailyEndTime
        delete payload.workTimeRaw
        this.submitLoading = true
        const api = this.form.id != null ? manualEditOrder(payload) : manualAddOrder(payload)
        api.then(() => {
          this.$message.success(this.form.id != null ? '修改成功' : '新增成功', 3)
          this.open = false
          this.$emit('ok')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>
